import i18n from '@/plugins/i18n';
import { formatPercent } from '@/utils/numberFormat';
import { clampPercent, isProgressComplete } from '@/utils/objectiveProgress';

/**
 * The prototype's `RingSVG`: a 60-tick dial, each tick a radial line drawn from `innerRadius`
 * to `outerRadius`, with the percentage in the middle.
 *
 * The library's `CircularProgress` cannot reproduce it: it derives both the tick length and the
 * tick width from a single `thickness` prop (`length = thickness * 0.5`, `width = thickness *
 * 0.4`), so the prototype's long-and-thin ticks (8 px long, 1.8 px wide at size 128) are out of
 * reach -- matching the width needs `thickness = 4.5`, which then draws 2.25 px stubs instead of
 * 8 px ticks. It also defaults to counter-clockwise and renders its label at a size and weight
 * the prototype does not use.
 */

const TOTAL_TICKS = 60;
const TICK_WIDTH = 1.8;
// SVG `stroke` reads custom properties, so the dial takes the module's own tokens rather than
// repeating their hex values -- retuning the palette retunes the ring.
const INACTIVE_COLOR = 'var(--color-obj-tint)';
const TURQUOISE = 'var(--color-obj-turquoise)';
const OVERDUE_RED = '#ef4444';

/** Ring radius, tick length and label size all scale with the ring, exactly as the prototype. */
const OUTER_RADIUS_INSET = 5;
const TICK_LENGTH_RATIO = 0.063;
const MIN_TICK_LENGTH = 6;
const FONT_SIZE_RATIO = 0.16;

interface ProgressRingProps {
    /** Percentage in `[0, 100]`; out-of-range server values are clamped. */
    value: number;
    /** Rendered width and height in pixels. */
    size: number;
    /**
     * Paints the ticks red instead of turquoise. A ring that reached 100% stays turquoise even
     * past its due date, mirroring the prototype (`complete` wins over `isOverdue`).
     */
    isOverdue?: boolean;
    ariaLabel?: string;
    dataTestId?: string;
}

export default function ProgressRing({
    value,
    size,
    isOverdue = false,
    ariaLabel,
    dataTestId,
}: ProgressRingProps) {
    const percentage = clampPercent(value);
    const filledTicks = Math.round((percentage / 100) * TOTAL_TICKS);

    const center = size / 2;
    const outerRadius = size / 2 - OUTER_RADIUS_INSET;
    const tickLength = Math.max(MIN_TICK_LENGTH, Math.round(size * TICK_LENGTH_RATIO));
    const innerRadius = outerRadius - tickLength;
    const fontSize = Math.round(size * FONT_SIZE_RATIO);

    const isComplete = isProgressComplete(percentage);
    const activeColor = !isComplete && isOverdue ? OVERDUE_RED : TURQUOISE;

    return (
        <div
            className={'relative flex items-center justify-center'}
            style={{ width: size, height: size }}
            role={'progressbar'}
            aria-valuenow={percentage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={ariaLabel}
            data-testid={dataTestId}
        >
            <svg
                width={size}
                height={size}
                aria-hidden
            >
                {Array.from({ length: TOTAL_TICKS }).map((_unused, index) => {
                    // Clockwise from 12 o'clock, like the prototype.
                    const angle = (index / TOTAL_TICKS) * 2 * Math.PI - Math.PI / 2;
                    const cos = Math.cos(angle);
                    const sin = Math.sin(angle);

                    return (
                        <line
                            key={index}
                            x1={center + cos * innerRadius}
                            y1={center + sin * innerRadius}
                            x2={center + cos * outerRadius}
                            y2={center + sin * outerRadius}
                            stroke={index < filledTicks ? activeColor : INACTIVE_COLOR}
                            strokeWidth={TICK_WIDTH}
                            strokeLinecap={'round'}
                        />
                    );
                })}
            </svg>

            <div className={'absolute inset-0 flex items-center justify-center'}>
                <span
                    className={'text-navy leading-none font-bold tabular-nums'}
                    style={{ fontSize }}
                >
                    {formatPercent(percentage, i18n.language)}
                </span>
            </div>
        </div>
    );
}
